import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';

import { isEmpty } from 'lodash';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormWidget from '../../common/form/FormWidget';
import I18n from '../../common/i18n/I18n';
import { PEOPLE, PERSON } from '../../hiring/hiring';
import { formatItemForPerson } from '../../hiringManager/hireResourceUtils';
import ResourceOrderApproverEditor from '../../production/resource/orders/editor/resourceOrderApprovalsStep/ResourceOrderApproverEditor';
import PersonOptionRenderer from './PersonOptionRenderer';

/**
 * PersonFormField Component
 * Encapsulates the Person selection form field.
 */
type PersonFormFieldProps = {
    isRequestAvailsForUser: boolean;
    savePerson: (person: any) => void;
    quickAddPerson: (newValue: any, saveMutation: any, flag: boolean) => void;
    filters?: Record<string, any>;
    isMultiple?: boolean;
};

const PersonFormField = ({
    isRequestAvailsForUser,
    filters,
    savePerson,
    quickAddPerson,
    isMultiple = false
}: PersonFormFieldProps) => {
    const { state } = useContext(FormContext);
    const [contactToEdit, setContactToEdit] = useState(null);
    const peopleData = state[isMultiple ? PEOPLE : PERSON];

    useEffect(() => {

        if (!Array.isArray(peopleData)) {
            setContactToEdit(null);
            return;
        }

        const hasCreateOption = peopleData.some((c) => c.createOption);

        if (hasCreateOption) {
            setContactToEdit(null);
            return;
        }

        const contactWithMissingEmail = peopleData.find((contact) => !contact.contactInfo?.email?.address);
        setContactToEdit(contactWithMissingEmail);
    }, [peopleData]);

    return (
        <FormContainer>
            <FormItem>
                <FormWidget
                    disabled={!isRequestAvailsForUser || !isEmpty(contactToEdit)}
                    name={isMultiple ? PEOPLE : PERSON}
                    label={<I18n token="resourceInquiries.name" />}
                    component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                    entity={ENTITY_CONSTANTS.person}
                    OptionRenderer={PersonOptionRenderer}
                    getOption={(item) => formatItemForPerson(item)}
                    preload={false}
                    notOnlyRef={isRequestAvailsForUser}
                    filter={filters}
                    addOption={isRequestAvailsForUser}
                    saveMutation={savePerson}
                    onCreateHandle={(newValue, saveMutation) => quickAddPerson(newValue, saveMutation, true)}
                    tooltip={(option) => option.ref?.contact?.contactInfo?.email?.address}
                    multiple={isMultiple}
                />
            </FormItem>
            {isRequestAvailsForUser && (
                <FormItem hidden={isEmpty(contactToEdit)}>
                    <ResourceOrderApproverEditor
                        contact={contactToEdit}
                        setContact={setContactToEdit}
                        fieldNameKey={PEOPLE}
                    />
                </FormItem>
            )}
        </FormContainer>
    );
};

PersonFormField.propTypes = {
    isRequestAvailsForUser: PropTypes.bool.isRequired,
    filters: PropTypes.object,
    savePerson: PropTypes.func.isRequired,
    quickAddPerson: PropTypes.func.isRequired
};

PersonFormField.defaultProps = {
    filters: {}
};

export default PersonFormField;
